import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import { Box } from '@chakra-ui/react';
import {
  Chart as ChartJS,
  ScriptableContext,
  ChartOptions,
  ChartData,
  ChartTypeRegistry,
  ChartDataset,
  BubbleDataPoint,
  Point,
} from 'chart.js';
import { Loading } from 'components/Loading';
import { defaultColorArray } from 'utils/constants';
import { defaultChartPlugin } from 'utils/functions';

const MultiChart: React.FC<MultiChartProps> = ({
  labels,
  data,
  type = 'line',
  options,
  fill = true,
  w,
  loading,
}) => {
  const chartRef = useRef<ChartJS<typeof type>>(null);
  const [chartData, setChartData] = useState<ChartData<typeof type>>({
    datasets: data.map((element, index) => {
      return {
        type: `${element.type}` as const,
        label: element.label,
        borderColor: element.borderColor
          ? element.borderColor
          : element.fill
          ? Array.isArray(element.fill.color)
            ? element.fill.color[0]
            : element.fill.color
          : defaultColorArray[index],
        fill: !!element.fill,
        data: element.values,
      } as ChartDataset<
        keyof ChartTypeRegistry,
        (number | [number, number] | Point | BubbleDataPoint | null)[]
      >;
    }),
  });

  const chartDataFromData: ChartData<typeof type> = useMemo(
    () => ({
      datasets: data.map((element, index) => {
        return {
          type: `${element.type}` as const,
          label: element.label,
          borderColor: element.borderColor
            ? element.borderColor
            : element.fill
            ? Array.isArray(element.fill.color)
              ? element.fill.color[0]
              : element.fill.color
            : defaultColorArray[index],
          fill: !!element.fill,
          data: element.values,
        } as ChartDataset<
          keyof ChartTypeRegistry,
          (number | [number, number] | Point | BubbleDataPoint | null)[]
        >;
      }),
    }),
    [data],
  );

  const defaultOptions: ChartOptions<typeof type> = useMemo(
    () => ({
      maintainAspectRatio: false,
      responsive: true,
      plugins: defaultChartPlugin(data.length),
      scales: {
        x: {
          grid: {
            display: false,
          },
        },
        y: {
          grid: {
            display: true,
          },
        },
      },
    }),
    [data, labels],
  );

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) {
      return;
    }

    if (fill)
      setChartData({
        labels,
        grouped: true,
        datasets: chartDataFromData.datasets.map((dataset, index) => ({
          ...dataset,
          backgroundColor: (context: ScriptableContext<typeof type>) => {
            if (data[index].fill && Array.isArray(data[index].fill?.color)) {
              const ctx = context.chart.ctx;
              const chartArea = context.chart.chartArea;
              const gradient = ctx.createLinearGradient(
                0,
                chartArea.bottom,
                0,
                chartArea.top,
              );

              gradient.addColorStop(1, data[index].fill?.color?.[0] || 'white');
              gradient.addColorStop(0, data[index].fill?.color?.[1] || 'white');

              return gradient;
            }

            if (data[index].fill) return data[index].fill?.color || 'white';

            return (
              dataset.borderColor ||
              defaultColorArray[index].toString() ||
              'white'
            );
          },
        })),
      } as ChartData<typeof type>);
  }, [chartDataFromData]);

  return (
    <>
      {loading && <Loading />}
      <Box
        h="100%"
        w={w ? { base: w, md: '98%' } : '98%'}
        opacity={loading ? '50%' : '100%'}
      >
        <Chart
          ref={chartRef}
          type={type}
          options={options ? { ...defaultOptions, ...options } : defaultOptions}
          data={chartData}
        />
      </Box>
    </>
  );
};

export default MultiChart;
